import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Mail, Plus, Save, RotateCcw, Send, Eye, Sparkles, Code, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

/**
 * AdminEmailTemplatesTab — unified editor for every transactional /
 * lifecycle email the app sends.
 *
 * Templates: welcome, we_miss_you, blog_send_back, blog_approved,
 * resource_submission_received, sponsored_blog_receipt, friends_recap_weekly,
 * weekly_digest, plus any admin-created custom ones.
 *
 * Backend lives at /api/admin/email-templates/* (generic CRUD).
 */
const AdminEmailTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // template object
  const [draft, setDraft] = useState({ subject: '', html: '' });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [preview, setPreview] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTpl, setNewTpl] = useState({
    key: '', subject: '', html: '', description: '', category: 'custom',
  });

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiFetch('/api/admin/email-templates');
    if (error) toast.error(error);
    else if (data?.templates) {
      setTemplates(data.templates);
      // Auto-select first template so editor is never empty on first load.
      if (!selected && data.templates.length > 0) {
        const first = data.templates[0];
        setSelected(first);
        setDraft({ subject: first.subject, html: first.html });
      }
    }
    setLoading(false);
  }, [selected]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectTemplate = (tpl) => {
    setSelected(tpl);
    setDraft({ subject: tpl.subject, html: tpl.html });
    setPreview(false);
  };

  const handleSave = async () => {
    if (!selected) return;
    if (!draft.subject.trim() || draft.html.trim().length < 30) {
      toast.error('Subject required and HTML must be at least 30 chars');
      return;
    }
    setSaving(true);
    const { data, error } = await apiFetch(`/api/admin/email-templates/key/${selected.key}`, {
      method: 'PUT',
      body: {
        subject: draft.subject,
        html: draft.html,
        category: selected.category,
        description: selected.description,
        vars: selected.vars,
      },
    });
    setSaving(false);
    if (error) toast.error(error);
    else {
      toast.success('Template saved');
      // Refresh from server so is_custom flag flips.
      const fresh = data?.template;
      if (fresh) {
        setSelected(fresh);
        setTemplates((prev) => prev.map((t) => t.key === fresh.key ? fresh : t));
      }
    }
  };

  const handleReset = async () => {
    if (!selected) return;
    if (!window.confirm(`Reset "${selected.key}" to the default template? Your custom changes will be lost.`)) return;
    setSaving(true);
    const { error } = await apiFetch(`/api/admin/email-templates/key/${selected.key}`, {
      method: 'DELETE',
    });
    setSaving(false);
    if (error) toast.error(error);
    else {
      toast.success('Reset to default');
      await load();
    }
  };

  const handleSendTest = async () => {
    if (!selected) return;
    if (!testTo || !testTo.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSaving(true);
    const { error } = await apiFetch(`/api/admin/email-templates/key/${selected.key}/send-test`, {
      method: 'POST',
      body: { to: testTo },
    });
    setSaving(false);
    if (error) toast.error(error);
    else toast.success(`Test email sent to ${testTo}`);
  };

  const handleCreate = async () => {
    if (!newTpl.key.match(/^[a-z0-9_-]+$/)) {
      toast.error('Key must be lowercase letters, numbers, _ or -');
      return;
    }
    if (!newTpl.subject.trim() || newTpl.html.trim().length < 30) {
      toast.error('Subject and HTML are required (HTML ≥30 chars)');
      return;
    }
    setSaving(true);
    const { data, error } = await apiFetch(`/api/admin/email-templates/key/${newTpl.key}`, {
      method: 'PUT',
      body: { ...newTpl },
    });
    setSaving(false);
    if (error) toast.error(error);
    else {
      toast.success('New template created');
      setShowCreate(false);
      setNewTpl({ key: '', subject: '', html: '', description: '', category: 'custom' });
      await load();
      // Select the just-created one.
      if (data?.template) {
        setSelected(data.template);
        setDraft({ subject: data.template.subject, html: data.template.html });
      }
    }
  };

  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || 'custom';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <Card data-testid="email-templates-tab">
      <CardHeader>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#00A896]" />
              Email templates
            </CardTitle>
            <CardDescription>
              Edit any system email below, send yourself a test, or create a brand-new custom template.
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => setShowCreate(true)}
            className="bg-[#00C9B3] hover:bg-[#00B89F] text-[#0d1f2d]"
            data-testid="email-tpl-new-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New template
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 inline animate-spin mr-1" /> Loading…</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            {/* Template list */}
            <div className="space-y-3" data-testid="email-tpl-list">
              {Object.entries(grouped).map(([cat, list]) => (
                <div key={cat}>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 font-semibold">{cat}</p>
                  <div className="space-y-1">
                    {list.map((t) => {
                      const isSelected = selected?.key === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => selectTemplate(t)}
                          data-testid={`email-tpl-item-${t.key}`}
                          className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                            isSelected
                              ? 'border-[#00C9B3] bg-[#00C9B3]/10'
                              : 'border-border hover:bg-muted/40'
                          }`}
                        >
                          <div className="text-sm font-medium truncate">{t.key}</div>
                          {t.is_custom && (
                            <Badge variant="outline" className="text-[9px] mt-0.5">customized</Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Editor */}
            {selected ? (
              <div className="space-y-3" data-testid="email-tpl-editor">
                <div className="flex items-start justify-between gap-2 flex-wrap pb-2 border-b">
                  <div>
                    <h3 className="font-semibold text-base">{selected.key}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{selected.description}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreview((p) => !p)}
                      data-testid="email-tpl-preview-toggle"
                    >
                      {preview ? <Code className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {preview ? 'HTML' : 'Preview'}
                    </Button>
                  </div>
                </div>

                {selected.vars?.length > 0 && (
                  <div className="rounded border bg-muted/20 p-2">
                    <p className="text-[10px] uppercase text-muted-foreground font-semibold mb-1">Available variables</p>
                    <div className="flex flex-wrap gap-1">
                      {selected.vars.map((v) => (
                        <code
                          key={v}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-[#0d1f2d] text-[#00E5CC] cursor-pointer hover:opacity-80"
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${v}}}`);
                            toast.success(`Copied {{${v}}} to clipboard`);
                          }}
                          title={`Click to copy {{${v}}}`}
                        >
                          {`{{${v}}}`}
                        </code>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="tpl-subject" className="text-xs">Subject line</Label>
                  <Input
                    id="tpl-subject"
                    value={draft.subject}
                    onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                    data-testid="email-tpl-subject-input"
                  />
                </div>

                <div>
                  <Label htmlFor="tpl-html" className="text-xs">HTML body</Label>
                  {preview ? (
                    <div className="rounded-md p-1 bg-slate-200 dark:bg-slate-700 ring-1 ring-slate-300 dark:ring-slate-600">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-300 px-2 py-1">
                        Live email preview — recipients see exactly this
                      </p>
                      <div
                        className="rounded border bg-white p-3 max-h-[400px] overflow-y-auto text-sm"
                        style={{ color: '#0d1f2d' }}
                        data-testid="email-tpl-preview"
                        dangerouslySetInnerHTML={{ __html: draft.html }}
                      />
                    </div>
                  ) : (
                    <Textarea
                      id="tpl-html"
                      value={draft.html}
                      onChange={(e) => setDraft({ ...draft, html: e.target.value })}
                      rows={14}
                      className="font-mono text-xs"
                      data-testid="email-tpl-html-input"
                    />
                  )}
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#00C9B3] hover:bg-[#00B89F] text-[#0d1f2d]"
                    data-testid="email-tpl-save-btn"
                  >
                    <Save className="h-4 w-4 mr-1" /> Save
                  </Button>
                  {selected.is_custom && (
                    <Button
                      variant="outline"
                      onClick={handleReset}
                      disabled={saving}
                      data-testid="email-tpl-reset-btn"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" /> Reset to default
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Input
                    type="email"
                    placeholder="Send test to…"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    className="w-48 h-9"
                    data-testid="email-tpl-test-input"
                  />
                  <Button
                    variant="outline"
                    onClick={handleSendTest}
                    disabled={saving || !testTo}
                    data-testid="email-tpl-send-test-btn"
                  >
                    <Send className="h-4 w-4 mr-1" /> Send test
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Pick a template on the left to start editing.</p>
            )}
          </div>
        )}
      </CardContent>

      {/* New template dialog */}
      {showCreate && (
        <Dialog open onOpenChange={(o) => !o && setShowCreate(false)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-tpl-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#7C3AED]" />
                Create a custom email template
              </DialogTitle>
              <DialogDescription className="text-xs">
                Define a new template that you can send via the API or use as a starting point for future emails.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="new-key" className="text-xs">Key (lowercase letters/numbers/_-)</Label>
                <Input
                  id="new-key"
                  value={newTpl.key}
                  onChange={(e) => setNewTpl({ ...newTpl, key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                  placeholder="e.g. monthly_milestone"
                  data-testid="new-tpl-key-input"
                />
              </div>
              <div>
                <Label htmlFor="new-desc" className="text-xs">Description (for your reference)</Label>
                <Input
                  id="new-desc"
                  value={newTpl.description}
                  onChange={(e) => setNewTpl({ ...newTpl, description: e.target.value })}
                  placeholder="When does this email get sent?"
                  data-testid="new-tpl-desc-input"
                />
              </div>
              <div>
                <Label htmlFor="new-subject" className="text-xs">Subject</Label>
                <Input
                  id="new-subject"
                  value={newTpl.subject}
                  onChange={(e) => setNewTpl({ ...newTpl, subject: e.target.value })}
                  data-testid="new-tpl-subject-input"
                />
              </div>
              <div>
                <Label htmlFor="new-html" className="text-xs">HTML body</Label>
                <Textarea
                  id="new-html"
                  value={newTpl.html}
                  onChange={(e) => setNewTpl({ ...newTpl, html: e.target.value })}
                  rows={10}
                  className="font-mono text-xs"
                  placeholder={'<div>...</div>'}
                  data-testid="new-tpl-html-input"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Use <code>{'{{var_name}}'}</code> placeholders.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCreate(false)} data-testid="new-tpl-cancel-btn">Cancel</Button>
              <Button
                onClick={handleCreate}
                disabled={saving}
                className="bg-[#00C9B3] hover:bg-[#00B89F] text-[#0d1f2d]"
                data-testid="new-tpl-create-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Create template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

export default AdminEmailTemplatesTab;
